import { memo } from 'react';
import { useIntl } from 'react-intl';
import { useStyles } from 'uinix-ui';

import { SharedChipProps } from '../../types/SharedChipProps';
import { StatusType } from '../../types/StatusType';
import { TextColor } from '../../types/TextColor';
import { Box } from '../Box/Box';
import { Icon } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';
import { Layout } from '../Layout/Layout';
import { LoadingShimmer } from '../LoadingShimmer/LoadingShimmer';
import { MarkedText } from '../MarkedText/MarkedText';
import { TruncateText } from '../Text/subcomponents/TruncateText';
import { Text } from '../Text/Text';
import { Tooltip } from '../Tooltip/Tooltip';
import { usePreset } from './usePreset';

/** Ai `border-box` width (also the default when `width` is omitted) and height. */
const CHIP_AI_WIDTH = '62px';
const CHIP_AI_HEIGHT = '20px';

const AI_LABEL_OVERFLOW = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'clip',
  whiteSpace: 'nowrap',
} as const;

const STATUS_TEXT_COLOR: Record<StatusType, TextColor> = {
  active: 'status.active',
  ai: 'text.primary',
  danger: 'status.danger',
  alert: 'status.alert',
  inactive: 'text.secondary',
  info: 'text.secondary',
  new: 'status.active',
  success: 'status.success',
  warning: 'status.warning.text',
};

export const Chip = memo((props: SharedChipProps) => {
  const intl = useIntl();
  const styles = useStyles();

  const {
    disabled,
    icon: propIcon,
    isLoading,
    mode,
    preset,
    status: propStatus,
    text: propText,
    tooltip,
    width: initialWidth,
    search,
    onRemove,
    onClick,
  } = props;

  const presetResult = usePreset(preset);

  const icon = presetResult.icon ?? propIcon;
  const status = presetResult.status ?? propStatus ?? 'info';
  const text = presetResult.text ?? propText;

  const isAi = mode === 'ai';
  const showRemove = Boolean(onRemove && !onClick);
  const width = initialWidth ?? (isAi ? CHIP_AI_WIDTH : 'min-content');

  const isTagMode = mode === 'tag';
  const usesStatusTextColor = mode === 'ai';

  const border = isTagMode || isAi ? undefined : 'border';
  const borderRadius = isTagMode ? 'm' : 'pill';

  const textContent = isAi ? (
    <Text
      flexShrink={0}
      variant="tiny"
      whiteSpace="nowrap"
      styles={AI_LABEL_OVERFLOW}
      color={usesStatusTextColor ? STATUS_TEXT_COLOR[status] : undefined}
    >
      <MarkedText text={text} search={search ?? ''} />
    </Text>
  ) : (
    <TruncateText
      flex={onClick ? 1 : undefined}
      minWidth={0}
      variant="tiny"
      whiteSpace="nowrap"
      color={usesStatusTextColor ? STATUS_TEXT_COLOR[status] : undefined}
    >
      <MarkedText text={text} search={search ?? ''} />
    </TruncateText>
  );

  const iconContent = icon ? <Icon icon={icon} ml={-1} /> : null;

  const content = isLoading ? (
    <LoadingShimmer height="calc(100% - 8px)" width="100%" />
  ) : (
    <>
      {onClick && !disabled ? (
        <Layout
          as="button"
          type="button"
          align="center"
          flex={!isAi ? 1 : undefined}
          justify={isAi ? 'center' : undefined}
          minWidth={0}
          overflow="hidden"
          data-eds-styles-disable-interactable
          styles={componentStyles.clickable}
          onClick={() => onClick(props)}
        >
          {iconContent}
          {textContent}
        </Layout>
      ) : (
        <>
          {iconContent}
          {textContent}
        </>
      )}

      {showRemove && (
        <Box mr={-1}>
          <IconButton
            icon="x"
            size="s"
            tooltip={intl.formatMessage({
              id: 'Chip.remove',
              defaultMessage: 'Remove',
              description: 'Tooltip for removing a chip',
            })}
            onClick={onRemove}
          />
        </Box>
      )}
    </>
  );

  return (
    <Tooltip tooltip={tooltip}>
      <Layout
        role="listitem"
        align="center"
        border={border}
        borderRadius={borderRadius}
        disabled={disabled}
        spacing={1}
        styles={[
          componentStyles.base,
          ...(isAi
            ? [
                {
                  ...componentStyles.ai,
                  maxWidth: width,
                  minWidth: width,
                  width,
                },
              ]
            : []),
          styles.colors.status,
          ...(onClick && !disabled ? [componentStyles.raiseOnHover] : []),
        ]}
        styleProps={{
          boxSizing: showRemove ? 'content-box' : 'border-box',
          status,
          width,
        }}
      >
        {content}
      </Layout>
    </Tooltip>
  );
});

const componentStyles = {
  base: ({
    boxSizing,
    width,
  }: {
    boxSizing: 'border-box' | 'content-box';
    width: string;
  }) => ({
    border: 'none',
    boxSizing,
    height: 'height.s',
    maxWidth:
      boxSizing === 'content-box' ? 'chip.max-width' : 'chip.auto.max-width',
    outline: 'none',
    paddingLeft: 3,
    paddingRight: 3,
    width,
  }),
  ai: {
    flexGrow: 0,
    flexShrink: 0,
    height: CHIP_AI_HEIGHT,
    justifyContent: 'center',
    overflow: 'hidden',
    paddingBottom: 1,
    paddingLeft: 2,
    paddingRight: 2,
    paddingTop: 1,
  },
  clickable: {
    background: 'none',
    border: 'none',
    borderRadius: 'pill',
    boxShadow: 'none',
    cursor: 'pointer',
    font: 'inherit',
    minWidth: 0,
    padding: 0,
  },
  raiseOnHover: {
    transition: 'box-shadow 0.15s ease',
    '&:hover': {
      boxShadow: 'raised',
    },
  },
};
